import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Send, AlertTriangle, AlertCircle, Info, Eye, ArrowRight } from 'lucide-react';
import type { Issue } from '@/models/issue';
import type { UploadResult } from '@/models/auth';
import { issueApi } from '@/services/issueApi';
import { useIssueCounts } from '@/controllers/useIssues';
import { IssueRow } from './components/IssueRow';
import { ScreenshotDrop } from './components/ScreenshotDrop';

export function IntakeView() {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [recent, setRecent] = useState<Issue[]>([]);
  const [listError, setListError] = useState('');
  const [screenshot, setScreenshot] = useState<UploadResult | null>(null);
  const counts = useIssueCounts();

  useEffect(() => {
    issueApi
      .list({ limit: 8 })
      .then((r) => setRecent(r.items))
      .catch((e) => setListError(e instanceof Error ? e.message : 'List fehlgeschlagen'));
  }, []);

  async function submit() {
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await issueApi.intake(text.trim(), screenshot?.id);
      navigate(`/i/${res.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submit fehlgeschlagen');
      setSubmitting(false);
    }
  }

  const sevCount = (sev: string) =>
    recent.filter((i) => i.severity === sev).length;

  return (
    <div className="max-w-4xl">
      <h1
        className="text-[32px] tracking-tight mb-2"
        style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, color: 'var(--text-body)' }}
      >
        Intake
      </h1>
      <p className="text-[15px] text-[var(--text-muted)] mb-10">
        Beschreibe den Bug, das Problem oder den Wunsch — freier Text. Die KI extrahiert
        Titel, Severity, Component und Tags automatisch.
      </p>

      {/* Stats */}
      <div className="section-title">Übersicht</div>
      <div className="grid grid-cols-5 gap-6 mb-12">
        <div className="stat-card" style={{ borderLeft: '2px solid var(--text-primary)', paddingLeft: 16 }}>
          <div className="flex items-center gap-2 mb-2">
            <Eye size={14} strokeWidth={1.5} />
            <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)]">Total</span>
          </div>
          <div className="text-[28px] font-light tabular-nums">{counts.loading ? '—' : counts.total}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '2px solid var(--color-info)', paddingLeft: 16 }}>
          <div className="flex items-center gap-2 mb-2">
            <Info size={14} strokeWidth={1.5} style={{ color: 'var(--color-info)' }} />
            <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)]">Open</span>
          </div>
          <div className="text-[28px] font-light tabular-nums">{counts.loading ? '—' : counts.open}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '2px solid var(--color-error)', paddingLeft: 16 }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={14} strokeWidth={1.5} style={{ color: 'var(--color-error)' }} />
            <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)]">Critical</span>
          </div>
          <div className="text-[28px] font-light tabular-nums">{sevCount('critical')}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '2px solid var(--color-warning)', paddingLeft: 16 }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} strokeWidth={1.5} style={{ color: 'var(--color-warning)' }} />
            <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)]">Major</span>
          </div>
          <div className="text-[28px] font-light tabular-nums">{sevCount('major')}</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '2px solid var(--color-success)', paddingLeft: 16 }}>
          <div className="flex items-center gap-2 mb-2">
            <Send size={14} strokeWidth={1.5} style={{ color: 'var(--color-success)' }} />
            <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)]">Fixed</span>
          </div>
          <div className="text-[28px] font-light tabular-nums">
            {recent.filter((i) => i.status === 'fixed').length}
          </div>
        </div>
      </div>

      {/* Intake */}
      <div className="section-title">Neuer Issue</div>
      <div className="mb-12">
        <label
          htmlFor="intake-text"
          className="block text-[10px] tracking-[0.18em] uppercase text-[var(--text-muted)] mb-2"
        >
          Was ist los?
        </label>
        <textarea
          id="intake-text"
          autoFocus
          placeholder="Bug, Problem, Wunsch — freier Text. ⌘/Ctrl + Enter zum Senden."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
          }}
          disabled={submitting}
          className="w-full p-4 text-[15px] font-[var(--font-body)] resize-y"
          style={{
            minHeight: '11rem',
            background: 'var(--bg-inset)',
            color: 'var(--text-body)',
            border: '1px solid var(--border)',
            borderRadius: 0,
            outline: 'none',
            lineHeight: 1.6,
          }}
        />
        <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
          <ScreenshotDrop onUploaded={setScreenshot} disabled={submitting} />
          <button
            className="btn-primary inline-flex items-center gap-2"
            onClick={submit}
            disabled={!text.trim() || submitting}
            style={{ background: 'var(--accent)', color: 'white' }}
          >
            {submitting ? (
              <>
                <span
                  className="w-3 h-3 rounded-full border-2 border-white border-t-transparent"
                  style={{ animation: 'spin 0.7s linear infinite' }}
                />
                KI klassifiziert…
              </>
            ) : (
              <>
                <Send size={14} strokeWidth={1.8} />
                Senden
              </>
            )}
          </button>
        </div>
        {error && (
          <div
            className="mt-4 p-3 text-[12px] border-l-2"
            style={{
              borderColor: 'var(--color-error)',
              background: 'var(--color-error-bg)',
              color: 'var(--color-error)',
            }}
          >
            {error}
          </div>
        )}
      </div>

      {/* Recent */}
      <div className="section-title flex items-center justify-between">
        <span>Zuletzt</span>
        <Link
          to="/list"
          className="text-[10px] uppercase tracking-[0.15em] text-[var(--accent)] hover:underline inline-flex items-center gap-1 normal-case"
          style={{ letterSpacing: '0.18em' }}
        >
          alle <ArrowRight size={11} strokeWidth={1.8} />
        </Link>
      </div>
      {listError && (
        <div className="p-3 text-[12px] border-l-2"
          style={{
            borderColor: 'var(--color-error)',
            background: 'var(--color-error-bg)',
            color: 'var(--color-error)',
          }}>
          {listError}
        </div>
      )}
      {!listError && recent.length === 0 && (
        <div className="py-10 text-center text-[13px] text-[var(--text-muted)] border border-dashed border-[var(--border)]">
          noch keine issues — sei die*der erste
        </div>
      )}
      {recent.length > 0 && (
        <div>
          {recent.map((i) => (
            <IssueRow key={i.id} issue={i} />
          ))}
        </div>
      )}
    </div>
  );
}
